import React from 'react';
import { JcSize, JcUtils } from '../utils/JcUtils';
import { JcBoxProps, JcColumn, JcRow } from './JcBox';
import { JcIcon, JcIconType } from './icon/JcIcon';
import { JcBuiltInIcon } from './icon/JcBuiltInIcons';
import { JcLabel } from './JcLabel';
import { JcNote } from './JcNote';

export enum JcBannerTone {
  NEUTRAL = "neutral",
  HIGHLIGHT = "highlight",
  SUCCESS = "success",
  // INFO / WARNING / ERROR can be added: a .jc-banner-<tone> block in
  // JcSDK.css and a TONE_ICON entry.
}

const TONE_ICON: Record<JcBannerTone, JcBuiltInIcon> = {
  [JcBannerTone.NEUTRAL]: JcBuiltInIcon.INFORMATION_CIRCLE,
  [JcBannerTone.HIGHLIGHT]: JcBuiltInIcon.STAR,
  [JcBannerTone.SUCCESS]: JcBuiltInIcon.CHECK_CIRCLE,
};

export interface JcBannerProps extends Omit<JcBoxProps, "spacing" | "alignCenter" | "justifyCenter" | "flexGrow" | "children"> {
  readonly title: string;
  readonly description?: string;
  readonly tone?: JcBannerTone;
  /**
   * Replaces the tone's default icon in the leading slot — pass a
   * {@link JcLiveIndicator} for a live/in-progress banner, or any node. Inherits
   * the tone accent colour.
   */
  readonly leading?: React.ReactNode;
  /** Trailing call-to-action (link/button). */
  readonly action?: React.ReactNode;
}

/**
 * A prominent contextual callout (leading indicator + title + optional
 * description/action, accented by tone). For terse inline messages use JcNotice.
 */
export const JcBanner: React.FunctionComponent<JcBannerProps> = (props: JcBannerProps) => {

  const tone = props.tone ?? JcBannerTone.NEUTRAL;

  const boxProps = JcUtils.otherProps(props);
  const commonProps = JcUtils.commonProps(props, [
    "jc-banner-" + tone,
    "jc-banner",
  ]);

  const leading = props.leading ?? (
    <JcIcon
      type={JcIconType.BUILT_IN}
      icon={TONE_ICON[tone]}
      size={JcSize.SMALL}
    />
  );

  return <JcRow {...commonProps} {...boxProps} alignCenter spacing>
    <JcRow className="jc-banner-lead-row" alignCenter flexGrow>
      <div className="jc-banner-lead">{leading}</div>
      <JcColumn className="jc-banner-text">
        <JcLabel className="jc-banner-title" label={props.title} />
        {props.description != null && <JcNote text={props.description} />}
      </JcColumn>
    </JcRow>
    {props.action != null && <div className="jc-banner-action">{props.action}</div>}
  </JcRow>
}
